import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'
import { profileService } from '@/services/profile.service'
import { useAuthStore } from '@/stores/auth'
import { useTenantDataRevision } from '@/composables/useTenantDataRevision'
import type { GateId, MilestoneKey, MilestoneState, UserTier } from '@/types/milestones'
import { MILESTONE_ORDER, computeTierFromKeys, nextMilestoneKey } from '@/types/milestones'

/** One user action fires several writes plus a list reload — collapse them into one refetch. */
const REFRESH_DEBOUNCE_MS = 600

export const useMilestonesStore = defineStore('milestones', () => {
  const completedKeys = ref<MilestoneKey[]>([])
  const unackedKeys = ref<MilestoneKey[]>([])
  const tier = ref<UserTier>('starter')
  const nextKey = ref<MilestoneKey | null>(null)
  const gates = ref<Partial<Record<GateId, boolean>>>({})
  const showAllFeatures = ref(false)
  const loading = ref(false)
  const loaded = ref(false)

  let refreshTimer: ReturnType<typeof setTimeout> | null = null

  const progressTotal = computed(() => MILESTONE_ORDER.length)
  const progressDone = computed(() => completedKeys.value.length)

  function applyState(state: MilestoneState | null) {
    if (!state) return
    completedKeys.value = state.completed_keys ?? []
    unackedKeys.value = state.unacked_keys ?? []
    tier.value = state.tier ?? computeTierFromKeys(completedKeys.value)
    nextKey.value = state.next_key ?? nextMilestoneKey(completedKeys.value)
  }

  function applyGates(state: Partial<Record<GateId, boolean>> | null) {
    gates.value = state ?? {}
  }

  function hasCompleted(key: MilestoneKey | string): boolean {
    return completedKeys.value.includes(key as MilestoneKey)
  }

  /**
   * Whether a navigation gate is satisfied by the tenant's own documents.
   *
   * Fails open: an unloaded or failed gate fetch leaves the item unlocked.
   * Locking on missing data is what made the sidebar unusable in the first
   * place, so absence of evidence is never treated as evidence of absence.
   */
  function isGateOpen(gate?: GateId): boolean {
    if (showAllFeatures.value) return true
    if (!gate) return true
    return gates.value[gate] !== false
  }

  async function fetchSettings() {
    try {
      const settings = await profileService.getSettings()
      showAllFeatures.value = settings?.show_all_features === true
    } catch {
      showAllFeatures.value = false
    }
  }

  async function fetchNavigationGates() {
    try {
      applyGates(await profileService.getNavigationGates())
    } catch {
      applyGates(null)
    }
  }

  async function fetchMilestones(options?: { force?: boolean }) {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      clear()
      return
    }

    if (loaded.value && !options?.force) return

    loading.value = true
    try {
      const [state] = await Promise.all([
        profileService.getMilestones(),
        fetchSettings(),
        fetchNavigationGates(),
      ])
      applyState(state)
      loaded.value = true
    } finally {
      loading.value = false
    }
  }

  async function ackMilestone(key: MilestoneKey) {
    await profileService.ackMilestone(key)
    unackedKeys.value = unackedKeys.value.filter((k) => k !== key)
  }

  const { revision: tenantDataRevision } = useTenantDataRevision()

  /**
   * Gates and milestones are derived from the tenant's documents, so a write on
   * any page can lift a padlock. Without this the sidebar keeps the locks it saw
   * at app boot until the user reloads, and the unlock toast never fires for a
   * milestone completed after login.
   */
  watch(tenantDataRevision, () => scheduleRefresh())

  function scheduleRefresh() {
    cancelScheduledRefresh()
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      void fetchMilestones({ force: true })
    }, REFRESH_DEBOUNCE_MS)
  }

  function cancelScheduledRefresh() {
    if (refreshTimer === null) return
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  onScopeDispose(cancelScheduledRefresh)

  function clear() {
    cancelScheduledRefresh()
    completedKeys.value = []
    unackedKeys.value = []
    tier.value = 'starter'
    nextKey.value = null
    gates.value = {}
    showAllFeatures.value = false
    loaded.value = false
  }

  return {
    completedKeys,
    unackedKeys,
    tier,
    nextKey,
    gates,
    showAllFeatures,
    loading,
    loaded,
    progressTotal,
    progressDone,
    hasCompleted,
    isGateOpen,
    fetchMilestones,
    fetchSettings,
    fetchNavigationGates,
    ackMilestone,
    clear,
    applyState,
    applyGates,
  }
})
